import {
    Alert,
    Checkbox,
    Header,
    Link,
    Pagination,
    SpaceBetween,
    Spinner,
    Table,
    TableProps
} from '@amzn/awsui-components-react';
import React, { useEffect, useMemo, useState } from 'react';
import { RebootEvent, WorkflowExecutionInfo } from '../../../../types';
import DetailPopover from './DetailPopover';
import ExecutionSelector from './ExecutionSelector';
import { LOG_GROUP_PREFIX } from '../hooks/useWorkflow';

const PAGE_SIZE = 10;

const processNestedJson = (obj: any): any => {
    if (!obj || typeof obj !== 'object') return obj;

    const result = Array.isArray(obj) ? [...obj] : { ...obj };

    Object.keys(result).forEach(key => {
        const value = result[key];

        if (typeof value === 'string' &&
            ((value.startsWith('{') && value.endsWith('}')) ||
                (value.startsWith('[') && value.endsWith(']')))) {
            try {
                result[key] = JSON.parse(value);
            } catch (error) {
                console.warn(error);
            }
        }
    });

    return result;
};

const filterEvents = (
    events: RebootEvent[],
    selectedExecutionArn: string = '',
    showFailedOnly: boolean,
): RebootEvent[] => {
    return events.filter(event => {
        if (!event || !event.timestamp || !event.type || !event.execution_arn || !event.id) {
            return false;
        }

        if (selectedExecutionArn && event.execution_arn !== selectedExecutionArn) {
            return false;
        }

        if (showFailedOnly) {
            const typeHasFailed = event.type.toLowerCase().includes('failed');
            let stepHasFailed = false;
            
            if (event.details) {
                const parsedDetails = JSON.parse(event.details);
                const stepName = parsedDetails.name || '';
                stepHasFailed = stepName.toLowerCase().includes('failed');
            }

            if (!typeHasFailed && !stepHasFailed) {
                return false;
            }
        }

        return true;
    });
};

interface Props {
    events: RebootEvent[];
    loading: boolean;
    error: Error | null;
    executions: WorkflowExecutionInfo[];
    failedEvents: RebootEvent[];
    setLatestStep: (step: RebootEvent | undefined) => void;
    setFailedEvents: (events: RebootEvent[]) => void;
}

const WorkflowComponent: React.FC<Props> = ({
    events,
    loading,
    error,
    executions,
    failedEvents,
    setLatestStep,
    setFailedEvents,
}) => {
    const [selectedExecution, setSelectedExecution] = useState<string>('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [showFailed, setShowFailed] = useState<boolean>(false);

    useEffect(() => {
        if (!events || events.length === 0) {
            setLatestStep(undefined);
            setFailedEvents([]);
            return;
        }

        const filtered = filterEvents(events, '', true);
        setLatestStep(events[0]);
        setFailedEvents(filtered);
    }, [events, setLatestStep, setFailedEvents]);

    const filteredData = useMemo(() => {
        if (!events) return [];
        return filterEvents(events, selectedExecution, showFailed);
    }, [events, selectedExecution, showFailed, failedEvents]);

    const paginatedItems = useMemo(() => {
        const startIndex = (currentPage - 1) * PAGE_SIZE;
        const endIndex = startIndex + PAGE_SIZE;
        return filteredData.slice(startIndex, endIndex);
    }, [filteredData, currentPage]);

    const totalPages = useMemo(() => {
        return Math.ceil(filteredData.length / PAGE_SIZE);
    }, [filteredData]);

    const columnDefinitions: TableProps.ColumnDefinition<RebootEvent>[] = [
        {
            id: 'id',
            header: 'ID',
            cell: item => (
                <DetailPopover
                    event={item}
                    processedDetails={(() => {
                        try {
                            return item.details ? processNestedJson(JSON.parse(item.details || '{}')) : null;
                        } catch (error) {
                            console.error(error);
                            return item.details || null;
                        }
                    })()}
                >
                    <Link variant="primary" onFollow={e => e.preventDefault()}>
                        {item.id}
                    </Link>
                </DetailPopover>
            ),
        },
        {
            id: 'type',
            header: 'Type',
            cell: item => {
                const isFailed = item.type.toLowerCase().includes('fail');
                return <span style={{ color: isFailed ? 'red' : 'inherit' }}>{item.type}</span>;
            },
        },
        {
            id: 'step',
            header: 'Step',
            cell: item => {
                if (!item.details) return '-';
                try {
                    const parsedDetails = JSON.parse(item.details);
                    const stepName = parsedDetails.name || '-';
                    const isFailed = stepName.toLowerCase().includes('fail');
                    return (
                        <span style={{ color: isFailed ? 'red' : 'inherit' }}>{stepName}</span>
                    );
                } catch (error) {
                    console.warn(error)
                    return '-';
                }
            },
        },
        {
            id: 'logGroup',
            header: 'Log',
            cell: item => {
                if (!item.logGroup) return '-';
                return item.logGroup.replace(LOG_GROUP_PREFIX, '');
            },
        },
        {
            id: 'timestamp',
            header: 'Time',
            cell: item => new Date(item.timestamp).toLocaleString(),
        },
    ];

    return (
        <SpaceBetween direction="vertical" size="l">
            {error && (
                <Alert type="error" header="Error">
                    {error.message || "Unexpected error has occurred."}
                </Alert>
            )}
            <ExecutionSelector
                selected={selectedExecution}
                executions={executions}
                setExecution={setSelectedExecution}
            />
            <Table
                header={(
                    <Header
                        variant='h2'
                        actions={loading ? <Spinner size="normal" /> : (
                            <Checkbox checked={showFailed} onChange={() => { setShowFailed(!showFailed) }}>
                                Show Failed Only
                            </Checkbox>
                        )}
                    >
                        <SpaceBetween direction='horizontal' size='s'>
                            Workflow Events
                        </SpaceBetween>
                    </Header>
                )}
                items={paginatedItems}
                columnDefinitions={columnDefinitions}
                empty="No events found"
                loadingText="Loading workflow events..."
                wrapLines={true}
                pagination={
                    <Pagination
                        currentPageIndex={currentPage}
                        pagesCount={totalPages}
                        onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
                    />
                }
            />
        </SpaceBetween>
    );
};

export default WorkflowComponent;
